/** Pure Settings / CTA allowlist display mapping — no I/O. D4I-C. */

import { BUILTIN_CTA_HREFS } from "@vital/shared";
import type { StatusTone } from "./overview-ops";
import { cmsStrings } from "../terminology";

export type CtaDestinationKind = "approved" | "builtin";

export type CtaDestinationRow = {
  id: string;
  url: string;
  kind: CtaDestinationKind;
  /** True when present in local draft but not yet on server. */
  pendingSave: boolean;
};

export type CtaSortKey = "url" | "kind" | "status";

export type StatusDisplay = { label: string; tone: StatusTone; detail?: string };

export const SETTINGS_PAGE_NOTE = cmsStrings.settingsUi.pageNote;

export const KILL_SWITCH_LABEL = cmsStrings.killSwitch.title;

/** Truthful scope: remote CMS content only — login/CHAP untouched. */
export const KILL_SWITCH_SCOPE_NOTE = cmsStrings.killSwitch.scopeNote;

export const CTA_ALLOWLIST_NOTE = cmsStrings.settingsUi.ctaAllowlistNote;

export function ctaKindLabel(kind: CtaDestinationKind): string {
  return kind === "builtin" ? "پیش‌فرض" : "تأییدشده";
}

export function ctaStatusDisplay(row: CtaDestinationRow): StatusDisplay {
  if (row.kind === "builtin") {
    return {
      label: "پیش‌فرض فعال",
      tone: "info",
      detail: "بدون نیاز به تأیید مالک",
    };
  }
  if (row.pendingSave) {
    return {
      label: "در انتظار ذخیره",
      tone: "warning",
      detail: "هنوز روی سرور اعمال نشده",
    };
  }
  return {
    label: "تأیید و ذخیره شده",
    tone: "success",
    detail: "HTTPS مجاز برای CTA سفارشی",
  };
}

export function ctaAvailabilityDisplay(row: CtaDestinationRow): StatusDisplay {
  if (row.kind === "builtin") {
    return { label: "همیشه در دسترس", tone: "info" };
  }
  if (row.pendingSave) {
    return { label: "پیش‌نویس محلی", tone: "warning" };
  }
  return { label: "در فهرست تأیید", tone: "success" };
}

export function killSwitchStatusDisplay(active: boolean): StatusDisplay {
  return active
    ? {
        label: "فعال",
        tone: "danger",
        detail: "محتوای راه دور (اطلاعیه‌ها، پیشنهادها، تبلیغات ویژه) مخفی است",
      }
    : {
        label: "غیرفعال",
        tone: "success",
        detail: "محتوای منتشرشدهٔ راه دور قابل resolve است",
      };
}

export function buildCtaInventory(
  approvedUrls: readonly string[],
  savedUrls: readonly string[],
): CtaDestinationRow[] {
  const approved = approvedUrls.map((url) => ({
    id: `approved:${url}`,
    url,
    kind: "approved" as const,
    pendingSave: !savedUrls.includes(url),
  }));
  const builtins = BUILTIN_CTA_HREFS.map((url) => ({
    id: `builtin:${url}`,
    url,
    kind: "builtin" as const,
    pendingSave: false,
  }));
  return [...approved, ...builtins];
}

export function matchesCtaQuery(row: CtaDestinationRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [row.url, ctaKindLabel(row.kind), ctaStatusDisplay(row).label]
    .join(" ")
    .toLowerCase()
    .includes(q);
}

export function matchesCtaFilter(row: CtaDestinationRow, filter: string): boolean {
  if (filter === "all") return true;
  if (filter === "approved") return row.kind === "approved";
  if (filter === "builtin") return row.kind === "builtin";
  if (filter === "pending") return row.pendingSave;
  if (filter === "saved") return row.kind === "approved" && !row.pendingSave;
  return true;
}

export function filterAndSortCta(
  rows: CtaDestinationRow[],
  opts: { query: string; filter: string; sort: CtaSortKey },
): CtaDestinationRow[] {
  const filtered = rows.filter(
    (r) => matchesCtaQuery(r, opts.query) && matchesCtaFilter(r, opts.filter),
  );
  const sorted = [...filtered];
  sorted.sort((a, b) => {
    if (opts.sort === "kind") {
      const kindCmp = a.kind.localeCompare(b.kind);
      return kindCmp !== 0 ? kindCmp : a.url.localeCompare(b.url, "en");
    }
    if (opts.sort === "status") {
      const sa = ctaStatusDisplay(a).label;
      const sb = ctaStatusDisplay(b).label;
      const statusCmp = sa.localeCompare(sb, "fa");
      return statusCmp !== 0 ? statusCmp : a.url.localeCompare(b.url, "en");
    }
    return a.url.localeCompare(b.url, "en");
  });
  return sorted;
}

export function ctaInventoryCounts(rows: CtaDestinationRow[]): {
  total: number;
  approved: number;
  builtin: number;
  pending: number;
} {
  return {
    total: rows.length,
    approved: rows.filter((r) => r.kind === "approved").length,
    builtin: rows.filter((r) => r.kind === "builtin").length,
    pending: rows.filter((r) => r.pendingSave).length,
  };
}

export type RelatedSettingsLink = { id: string; label: string; to: string };

export function relatedSettingsLinks(): RelatedSettingsLink[] {
  return [
    { id: "promotions", label: "پیشنهادها", to: "/promotions" },
    { id: "announcements", label: "اطلاعیه‌ها", to: "/announcements" },
    { id: "sponsored", label: "تبلیغات ویژه", to: "/sponsored" },
    { id: "observability", label: "پایش", to: "/observability" },
    { id: "overview", label: "نمای کلی", to: "/" },
  ];
}
